import { useRef, type ReactElement } from 'react';
import { areaPath, buildSeriesSummary, isFiniteNumber, isolatedPoints, linePath, linearScale, pointPositions, smoothLinePath, valueDomain, type PathPoint } from '@vello/charts-core';
import { ChartEmpty, ChartLoading, cascade, colorOf, cx, defaultFormatValue, joinSummary, useElementWidth, useMotionState, useTweenedValues, type ChartToneName } from './kit';
import type { ChartBaseProps } from './line-chart';

export interface SparklineProps extends ChartBaseProps {
  /** The values in order, oldest first; `null` is a gap. */
  readonly values: readonly (number | null)[];
  /** A label per value for the accessible summary (default: the position, 1…n). */
  readonly labels?: readonly string[];
  /** Height in SVG units (default 40). */
  readonly height?: number;
  readonly area?: boolean;
  readonly smooth?: boolean;
  /** `neutral` is the ink series; success / warning / danger mark a state. */
  readonly tone?: ChartToneName;
  /** Dot on the last value (default true). */
  readonly endDot?: boolean;
  /** Keep zero in the domain (default false: a sparkline shows the shape of the movement). */
  readonly includeZero?: boolean;
}

const FALLBACK_WIDTH = 160;
const PAD = 4;

/** A tiny trend line with an optional soft area and an end dot; no axis, no tooltip (the value it stands next to carries the number). */
export function Sparkline(props: SparklineProps): ReactElement {
  const height = props.height ?? 40;
  if (props.loading === true) return <ChartLoading height={height} {...(props.loadingLabel !== undefined ? { label: props.loadingLabel } : {})} {...(props.className !== undefined ? { className: props.className } : {})} />;
  if (!props.values.some(isFiniteNumber)) return <ChartEmpty height={height} {...(props.emptyLabel !== undefined ? { label: props.emptyLabel } : {})} {...(props.className !== undefined ? { className: props.className } : {})} />;
  return <SparklinePlot {...props} height={height} />;
}

function SparklinePlot(props: SparklineProps & { height: number }): ReactElement {
  const { values, height } = props;
  const format = props.formatValue ?? defaultFormatValue;
  const smooth = props.smooth ?? true;
  const ref = useRef<HTMLDivElement>(null);
  const width = useElementWidth(ref, FALLBACK_WIDTH);
  const motion = useMotionState(props.animate);

  const clean = values.map((v): number | null => (isFiniteNumber(v) ? v : null));
  const target = valueDomain(clean, { includeZero: props.includeZero ?? false, nice: false, tickCount: 2 });
  const tweened = useTweenedValues([...clean, target.domain[0], target.domain[1]], motion);
  const drawn = tweened.slice(0, clean.length);
  const domain: [number, number] = [tweened[clean.length] ?? target.domain[0], tweened[clean.length + 1] ?? target.domain[1]];

  const baselineY = height - PAD;
  const y = linearScale(domain, [baselineY, PAD]);
  const xs = pointPositions(values.length, [PAD, Math.max(width - PAD, PAD + 1)]);
  const points: PathPoint[] = xs.map((x, i) => ({ x, y: drawn[i] === null || drawn[i] === undefined ? null : y(drawn[i] as number) }));
  const color = colorOf(0, props.tone);
  const lastIndex = points.reduce((acc, p, i) => (p.y !== null ? i : acc), -1);
  const last = lastIndex >= 0 ? points[lastIndex] : undefined;

  const summary =
    props.ariaLabel ??
    joinSummary([props.title, buildSeriesSummary(values.map((value, i) => ({ label: props.labels?.[i] ?? String(i + 1), value })), { format })]);

  return (
    <div ref={ref} className={cx('vello-chart', 'vello-chart--sparkline', props.className)} data-motion={motion}>
      <svg className="vello-chart__svg" width={width} height={height} viewBox={`0 0 ${width} ${height}`} role="img" aria-label={summary}>
        {(props.area ?? true) && <path className="vello-chart__area vello-chart__draw" d={areaPath(points, baselineY, { smooth })} fill={color} />}
        <path className="vello-chart__line vello-chart__draw" d={smooth ? smoothLinePath(points) : linePath(points)} stroke={color} style={cascade(0)} />
        {isolatedPoints(points).map((p) => (
          <circle key={`i${p.x}`} className="vello-chart__dot" cx={p.x} cy={p.y} r={3} fill={color} />
        ))}
        {(props.endDot ?? true) && last !== undefined && last.y !== null && <circle className="vello-chart__dot vello-chart__pop" cx={last.x} cy={last.y} r={4} fill={color} />}
      </svg>
    </div>
  );
}
